import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sprintGoalsApi } from '../services/api';

// ─── QUERIES ───────────────────────────────────────────────

// Get goals for a sprint (with mapped story keys)
export const useSprintGoals = (boardId, sprintId) => {
  return useQuery({
    queryKey: ['sprintGoals', boardId, sprintId],
    queryFn: () => sprintGoalsApi.getGoals(boardId, sprintId),
    enabled: !!boardId && !!sprintId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Get mapped issue keys for a sprint
export const useMappedIssueKeys = (boardId, sprintId) => {
  return useQuery({
    queryKey: ['mappedIssueKeys', boardId, sprintId],
    queryFn: () => sprintGoalsApi.getMappedIssueKeys(boardId, sprintId),
    enabled: !!boardId && !!sprintId,
    staleTime: 2 * 60 * 1000,
  });
};

// ─── MUTATIONS ─────────────────────────────────────────────

// Create a new goal
export const useCreateGoal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => sprintGoalsApi.createGoal(data),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['sprintGoals', variables.boardId, variables.sprintId],
      });
    },
  });
};

// Update a goal
export const useUpdateGoal = (boardId, sprintId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ goalId, data }) => sprintGoalsApi.updateGoal(goalId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['sprintGoals', boardId, sprintId],
      });
    },
  });
};

// Delete a goal
export const useDeleteGoal = (boardId, sprintId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (goalId) => sprintGoalsApi.deleteGoal(goalId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['sprintGoals', boardId, sprintId],
      });
      queryClient.invalidateQueries({
        queryKey: ['mappedIssueKeys', boardId, sprintId],
      });
    },
  });
};

// Add a story to a goal
export const useAddStoryToGoal = (boardId, sprintId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ goalId, issueKey }) => sprintGoalsApi.addStory(goalId, issueKey),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['sprintGoals', boardId, sprintId],
      });
      queryClient.invalidateQueries({
        queryKey: ['mappedIssueKeys', boardId, sprintId],
      });
    },
  });
};

// Remove a story from a goal
export const useRemoveStoryFromGoal = (boardId, sprintId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ goalId, issueKey }) => sprintGoalsApi.removeStory(goalId, issueKey),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['sprintGoals', boardId, sprintId],
      });
      queryClient.invalidateQueries({
        queryKey: ['mappedIssueKeys', boardId, sprintId],
      });
    },
  });
};

// Bulk update stories for a goal
export const useUpdateGoalStories = (boardId, sprintId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ goalId, issueKeys }) => sprintGoalsApi.updateGoalStories(goalId, issueKeys),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['sprintGoals', boardId, sprintId],
      });
      queryClient.invalidateQueries({
        queryKey: ['mappedIssueKeys', boardId, sprintId],
      });
    },
  });
};

// Reorder goals
export const useReorderGoals = (boardId, sprintId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (goalIds) => sprintGoalsApi.reorderGoals(boardId, sprintId, goalIds),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['sprintGoals', boardId, sprintId],
      });
    },
  });
};

// ─── HELPERS ───────────────────────────────────────────────

// Calculate goal progress based on sprint issues
export const calculateGoalProgress = (goal, sprintIssues) => {
  if (!goal?.stories?.length || !sprintIssues?.length) {
    return {
      total: 0,
      done: 0,
      inProgress: 0,
      todo: 0,
      percentage: 0,
      issues: [],
    };
  }

  const mappedIssues = sprintIssues.filter((issue) =>
    goal.stories.includes(issue.key)
  );

  const done = mappedIssues.filter((i) => i.statusCategory === 'done');
  const inProgress = mappedIssues.filter((i) => i.statusCategory === 'indeterminate');
  const todo = mappedIssues.filter((i) => i.statusCategory === 'new');

  return {
    total: mappedIssues.length,
    done: done.length,
    inProgress: inProgress.length,
    todo: todo.length,
    percentage: mappedIssues.length > 0
      ? Math.round((done.length / mappedIssues.length) * 100)
      : 0,
    issues: mappedIssues.map((i) => ({
      key: i.key,
      summary: i.summary,
      status: i.status,
      statusCategory: i.statusCategory,
      storyPoints: i.storyPoints,
      issueType: i.issueType,
    })),
  };
};
